import {
  Alert,
  AlertVariantEnum,
  Card,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  HTMLElementTypeEnum,
  IconButtonSection,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  PlusIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { usePostSupport } from '@/features/programs/api/post-support';
import { FormTypeEnum } from '@/types';
import { getParamsFromTableFilters } from '@/utils/table-filters';

import { useSupports } from '../api/get-supports';
import { useUpdateSupport } from '../api/update-support';
import { SupportsDrawerForm } from '../components/SupportsDrawerForm';
import { SupportsTable } from '../components/SupportsTable/SupportsTable';
import { useSupportsControllers } from '../hooks/useSupportsControllers';
import { useSupportsPermissions } from '../hooks/useSupportsPermissions';
import { useSupportsFilters } from '../stores/supports-filters';
import { TSupport } from '../types';

export default function SupportsPage() {
  const { t } = useTranslation();

  const { filters, paginationValues, clearFilters } = useSupportsFilters();

  /**
   * Resets table filters on page unmount.
   */
  useEffect(() => {
    return () => {
      clearFilters();
    };
  }, [clearFilters]);

  const { data: { data: supports = [], total: totalItems = 0 } = {}, isFetching } = useSupports({
    params: getParamsFromTableFilters({
      filters,
      paginationValues,
    }),
  });

  const { supportsDrawer, supportsDialog, handleDeleteSupport } = useSupportsControllers();
  const { canCreate, canUpdate, canDelete } = useSupportsPermissions();
  const { mutate: createSupport, isPending: isCreatingSupport } = usePostSupport();
  const { mutate: updateSupport, isPending: isUpdatingSupport } = useUpdateSupport();

  const onCreate = () => {
    if (!canCreate) return;
    supportsDrawer.open({ type: FormTypeEnum.CREATE, data: {} as TSupport });
  };

  const onUpdate = (support: TSupport) => {
    if (!canUpdate) return;
    supportsDrawer.open({ type: FormTypeEnum.UPDATE, data: support });
  };

  const onDelete = (support: TSupport) => {
    if (!canDelete) return;
    supportsDialog.open(support);
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Soportes')}
          </Typography>

          {canCreate && (
            <IconButtonSection
              className="mr-size-16"
              onClick={onCreate}
              internalProps={{
                icon: {
                  size: IconSizeEnum.SM,
                  type: PlusIcon,
                },
                text: {
                  value: t('Nuevo'),
                  color: FontColorEnum.Default,
                },
              }}
            />
          )}
        </div>
        <div className="mb-4">
          <Alert variant={AlertVariantEnum.INFO} withClose={false}>
            <Typography
              element={HTMLElementTypeEnum.Heading}
              fontSize={FontSizeEnum.Md}
              color={FontColorEnum.Inherit}
            >
              {t(
                'Solo es posible editar los soportes de Usuario. Los soportes de Sistema son configurados por el equipo de Acceso y se utilizan para funcionalidades específicas del sistema.'
              )}
            </Typography>
          </Alert>
        </div>

        <Card>
          <SupportsTable
            items={supports}
            isFetching={isFetching}
            totalItems={totalItems}
            onUpdate={canUpdate ? onUpdate : undefined}
            onDelete={canDelete ? onDelete : undefined}
          />
        </Card>
      </div>

      <SupportsDrawerForm
        isOpen={supportsDrawer.isOpen}
        onClose={supportsDrawer.close}
        onSubmit={data => {
          if (supportsDrawer.data?.type === FormTypeEnum.UPDATE) {
            updateSupport(
              { ...data, id: supportsDrawer.data.data.id },
              {
                onSuccess: () => {
                  supportsDrawer.close();
                },
              }
            );
          } else {
            createSupport(data, {
              onSuccess: () => {
                supportsDrawer.close();
              },
            });
          }
        }}
        isMutating={isCreatingSupport || isUpdatingSupport}
        //@ts-ignore
        data={supportsDrawer.data || undefined}
      />

      <ModalDialog
        isOpen={supportsDialog.isOpen}
        onClose={supportsDialog.close}
        onAction={handleDeleteSupport}
        heading={t('Eliminar soporte')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage title={t('¿Estás seguro de querer eliminar el soporte seleccionado?')} />
      </ModalDialog>
    </>
  );
}
